import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  LIST_QUERY_KEY,
  DOCUMENTS_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'
import {
  CrunchesPageEntities,
  shouldInvalidateCrunchesPage,
} from '@dc-shared-web/hooks/utils/shouldInvalidateCrunchesPage'

type ResendSharedDocumentParams = {
  id: string
}

type ResendSharedDocumentResponse = {
  status: 'error' | 'success'
  message?: string
  error?: string
}

const resendSharedDocument = async (
  apiClient: AxiosInstance,
  params: ResendSharedDocumentParams
): Promise<ResendSharedDocumentResponse> => {
  const { data } = await apiClient.postForm(
    `/ajax/share-link/resend/${params.id}`,
    {
      id: params.id,
    }
  )

  return data.data
}

export const useResendSharedDocument = (
  documentId: string,
  onSuccess: () => void
) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: ResendSharedDocumentParams) =>
      resendSharedDocument(apiClient, mutationParams),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries<CrunchesPageEntities>({
          queryKey: [DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY],
          refetchPage: (page) => {
            return shouldInvalidateCrunchesPage(page, documentId)
          },
        })

        toast({
          variant: 'success',
          title: 'Share link has been resent',
        })
        onSuccess()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error resending share link',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
